import React, { useEffect, useMemo, useState } from 'react';
import { DashboardTitle, Hi, WelcomeMessage } from '../../Constants';

import { useSelector, useDispatch } from 'react-redux';
import { getProfileActions } from '../../app/api/useDataManage/userActions';
import { selectCurrentLanguage } from '../../features/langSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetDashboardIngredientsQuery,
  useGetDashboardSummaryQuery,
  useGetDashboardFormulaQuery,
} from '../../features/customerApiSlice';
import { useCustomerCheckInMutation } from '../../features/auth/authApiSlice';
import { useGetIngredientsQuery } from '../../features/quizApiSlice';
import DashboardWrapper from './components/DashboardWrapper';
import { normalizeLanguageCode } from '../../utils/language';
import DashboardStatusLine from './dashboard/DashboardStatusLine';
import DashboardPrimaryAction from './dashboard/DashboardPrimaryAction';
import DashboardReferralCard from './dashboard/DashboardReferralCard';
import DashboardCheckInCard from './dashboard/DashboardCheckInCard';
import DashboardReviewCard from './dashboard/DashboardReviewCard';
import DashboardFormulaCard from './dashboard/DashboardFormulaCard';
import IngredientsModal from './components/IngredientsModal';
import DashboardFormulaEditor from './dashboard/DashboardFormulaEditor';
import DashboardSkeleton from './dashboard/DashboardSkeleton';
import RetentionGiftWheel from './dashboard/RetentionGiftWheel';
import { toast } from 'react-toastify';

const HIGH_SENTIMENT_BASE_URL =
  'https://www.reviews.io/store/landing_new_review?store=weriise.co&type=company&bypass_reputation=';
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'paused'];
const CANCELLED_SUBSCRIPTION_STATUSES = ['cancelled', 'canceled'];

function isSameDate(date1, date2) {
  if (!date1 || !date2) return false;

  const firstDate = new Date(date1);
  const secondDate = new Date(date2);

  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
}

function parseDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDisplayDate(value, locale = 'en-GB') {
  const date = parseDate(value);
  if (!date) return '';

  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDateLocale(language) {
  const languageCode = normalizeLanguageCode(language);

  if (languageCode === 'de') return 'de-DE';
  if (languageCode === 'fr') return 'fr-FR';

  return 'en-GB';
}

function getDurationNumber(duration) {
  const parsedDuration = parseInt(String(duration ?? ''), 10);
  return Number.isNaN(parsedDuration) ? null : parsedDuration;
}

function isOneOffDuration(duration) {
  return [30, 90].includes(getDurationNumber(duration));
}

function normalizeSubscriptionStatus(status) {
  return String(status || '').toLowerCase();
}

function getSubscriptionStatuses(subscription) {
  return [
    normalizeSubscriptionStatus(subscription?.status),
    normalizeSubscriptionStatus(subscription?.stripeData?.status),
  ].filter(Boolean);
}

function isSubscriptionCancelled(subscription) {
  return getSubscriptionStatuses(subscription).some((status) => CANCELLED_SUBSCRIPTION_STATUSES.includes(status));
}

function isSubscriptionActive(subscription) {
  if (isSubscriptionCancelled(subscription)) return false;
  return getSubscriptionStatuses(subscription).some((status) => ACTIVE_SUBSCRIPTION_STATUSES.includes(status));
}

function getSubscriptionStartDate(subscription) {
  return (
    subscription?.createdAt ||
    subscription?.created_at ||
    subscription?.started_at ||
    subscription?.updatedAt ||
    subscription?.updated_at
  );
}

function getCycleDay(subscription) {
  const startDate = parseDate(getSubscriptionStartDate(subscription));
  if (!startDate) return 1;

  const today = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.floor((today.setHours(0, 0, 0, 0) - startDate.setHours(0, 0, 0, 0)) / dayMs) + 1);
}

function buildReviewLinks({ email, quizId }) {
  const params = new URLSearchParams({
    user: '',
    order_id: quizId || '',
    email: email || '',
    products: '',
    l: '',
    video_campaign_id: '',
    customer_identifier: quizId || '',
  });
  const highSentimentUrl = `${HIGH_SENTIMENT_BASE_URL}&${params.toString()}`;
  // Internal navigation, so the address travels in router state instead of the
  // query string: a /reviews?email=... URL is reported verbatim to the Meta
  // pixel and Contentsquare on the resulting PageView.
  const lowSentimentUrl = '/reviews';

  return {
    1: lowSentimentUrl,
    2: lowSentimentUrl,
    3: lowSentimentUrl,
    4: highSentimentUrl,
    5: highSentimentUrl,
  };
}

const Dashboard = () => {
  const selectedLang = useSelector(selectCurrentLanguage);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => normalizeLanguageCode(localStorage.getItem('lang')));
  const [token] = useState(localStorage.getItem('customer_token'));
  const [customerCheckIn, { isLoading: isCheckingIn }] = useCustomerCheckInMutation();
  const {
    data: dashboardSummary,
    isLoading: isDashboardSummaryLoading,
  } = useGetDashboardSummaryQuery({ lang }, { skip: !token || !lang, refetchOnMountOrArgChange: true });
  const [isIngredientModalOpen, setIsIngredientModalOpen] = useState(false);
  const {
    data: ingredientsSummary,
    isLoading: isIngredientsLoading,
    isFetching: isIngredientsFetching,
  } = useGetDashboardIngredientsQuery({ lang }, { skip: !token || !lang || !isIngredientModalOpen });
  const { data: allIngredients } = useGetIngredientsQuery(lang, {
    skip: !token || !lang || !isIngredientModalOpen,
  });

  const [isFormulaEditorOpen, setIsFormulaEditorOpen] = useState(false);
  const { data: formulaEdit, refetch: refetchFormula } = useGetDashboardFormulaQuery(
    { lang }, { skip: !token || !lang, refetchOnMountOrArgChange: true },
  );

  const [lastQuizId, setLastQuizId] = useState(() => localStorage.getItem('quiz_id'));
  const [hasCheckedInLocally, setHasCheckedInLocally] = useState(false);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const latestRiiseSubscription = dashboardSummary?.latestRiiseSubscription || null;
  const hasActiveOmega3 = Boolean(dashboardSummary?.hasActiveOmega3);

  const isOneOffSub = isOneOffDuration(latestRiiseSubscription?.duration);
  const isCancelledSub = !isOneOffSub && isSubscriptionCancelled(latestRiiseSubscription);
  const isActiveRecurringSub = !isOneOffSub && isSubscriptionActive(latestRiiseSubscription);
  const customerType = isOneOffSub ? 'oneOff' : isActiveRecurringSub && !isCancelledSub ? 'active' : 'cancelled';
  const isEarlyCycle = customerType === 'active' && getCycleDay(latestRiiseSubscription) <= 10;

  const statusDate = useMemo(() => {
    const dateLocale = getDateLocale(lang || selectedLang);

    if (customerType === 'active') {
      return formatDisplayDate(
        latestRiiseSubscription?.stripeData?.upcoming_invoice?.next_payment_date ||
          latestRiiseSubscription?.stripeData?.current_period_end ||
          latestRiiseSubscription?.expirey,
        dateLocale,
      );
    }

    if (customerType === 'oneOff') {
      return formatDisplayDate(
        latestRiiseSubscription?.createdAt ||
          latestRiiseSubscription?.created_at ||
          latestRiiseSubscription?.updatedAt ||
          latestRiiseSubscription?.updated_at,
        dateLocale,
      );
    }

    return formatDisplayDate(
      latestRiiseSubscription?.updatedAt ||
        latestRiiseSubscription?.updated_at ||
        latestRiiseSubscription?.expirey ||
        latestRiiseSubscription?.createdAt ||
        latestRiiseSubscription?.created_at,
      dateLocale,
    );
  }, [customerType, lang, latestRiiseSubscription, selectedLang]);

  const isUserCheckedInToday = useMemo(() => {
    if (hasCheckedInLocally) return true;
    return Boolean(user?.checkIns?.find((item) => isSameDate(item?.date, new Date())));
  }, [hasCheckedInLocally, user]);

  const checkInStreak = user?.totalCheckIns || user?.checkIns?.length || 0;

  const focusTags = useMemo(() => {
    return (dashboardSummary?.formula?.focusArea || [])
      .map((item) => {
        return item?.answer || item?.name || String(item || '');
      })
      .filter(Boolean)
      .slice(0, 4);
  }, [dashboardSummary]);

  const ratingLinks = useMemo(
    () =>
      buildReviewLinks({
        email: user?.email,
        quizId: lastQuizId,
      }),
    [lastQuizId, user?.email],
  );

  useEffect(() => {
    const nextLang = normalizeLanguageCode(selectedLang) || normalizeLanguageCode(localStorage.getItem('lang'));
    if (nextLang) setLang(nextLang);
  }, [selectedLang]);

  useEffect(() => {
    if (dashboardSummary?.formula?.lastQuizId) {
      setLastQuizId(String(dashboardSummary.formula.lastQuizId));
    }
  }, [dashboardSummary?.formula?.lastQuizId]);

  useEffect(() => {
    dispatch(getProfileActions(token));
  }, [token, dispatch]);

  const handleCheckIn = async () => {
    if (customerType !== 'active' || isUserCheckedInToday) return;

    try {
      await customerCheckIn({
        checkIns: {
          date: new Date().toISOString(),
          feeling: 'good',
          message: '',
        },
      }).unwrap();
      setHasCheckedInLocally(true);
      dispatch(getProfileActions(token));
      toast.success(t('Dashboard_CheckedInToday'));
    } catch (err) {
      toast.error(err?.data?.message || t('Auth_GenericError'));
    }
  };

  const handleRetake = () => {
    navigate('/quiz', {
      state: { prefilledEmail: user?.email, prefilledName: user?.name },
    });
  };

  const handleReorder = () => {
    if (lastQuizId) {
      navigate(`/quiz?quizId=${lastQuizId}`);
      return;
    }

    handleRetake();
  };

  const handleSubscriptionAction = () => {
    navigate('/dashboard/subscription');
  };

  const handleExploreOmega3 = () => {
    navigate('/products/omega-3/addon');
  };

  const handleOpenHowTo = () => {
    window.open('https://www.weriise.co/how-to-take', '_blank', 'noopener,noreferrer');
  };

  const isDashboardLoading =
    isDashboardSummaryLoading;
  const ingredients = ingredientsSummary?.ingredients || [];
  const isIngredientModalLoading = isIngredientsLoading || isIngredientsFetching;

  return (
    <DashboardWrapper title={t(DashboardTitle)}>
      <div className="user-inner t-min-w-0 t-flex-1">
        {isDashboardLoading ? (
          <DashboardSkeleton />
        ) : (
          <div className="t-flex t-flex-col t-gap-5">
            <h1 className="t-m-0 t-font-serif t-text-[32px] t-font-normal t-leading-tight t-text-navy">
              {t(Hi)} {user?.name || ''}, {t(WelcomeMessage)}!
            </h1>

            <RetentionGiftWheel gift={dashboardSummary?.gift} />

            <DashboardStatusLine
              customerType={customerType}
              currentOrder={dashboardSummary?.currentOrder}
              displayDate={statusDate}
              onAction={customerType === 'active' ? handleSubscriptionAction : handleReorder}
              t={t}
            />

            <DashboardFormulaCard
              focusTags={focusTags}
              hasLastQuiz={Boolean(lastQuizId)}
              showOmegaUpsell={!hasActiveOmega3 && !isDashboardSummaryLoading}
              onRetake={handleRetake}
              formulaEdit={formulaEdit}
              onEditFormula={() => setIsFormulaEditorOpen(true)}
              onViewIngredients={() => setIsIngredientModalOpen(true)}
              onExploreOmega3={handleExploreOmega3}
              t={t}
            />

            <DashboardPrimaryAction
              customerType={customerType}
              isEarlyCycle={isEarlyCycle}
              isCheckedIn={isUserCheckedInToday}
              isCheckingIn={isCheckingIn}
              onCheckIn={handleCheckIn}
              onOpenHowTo={handleOpenHowTo}
              onReactivate={handleReorder}
              onSwitchToSubscription={handleReorder}
              t={t}
            />

            <DashboardReferralCard onShare={() => navigate('/dashboard/referralcode')} t={t} />

            <div className="t-grid t-grid-cols-1 t-gap-5 xl:t-grid-cols-2">
              <DashboardCheckInCard
                isActive={customerType === 'active'}
                isCheckedIn={isUserCheckedInToday}
                isLoading={isCheckingIn}
                streak={checkInStreak}
                onCheckIn={handleCheckIn}
                t={t}
              />
              <DashboardReviewCard
                ratingLinks={ratingLinks}
                t={t}
                navigate={navigate}
                reviewEmail={user?.email}
              />
            </div>
          </div>
        )}
      </div>
      {isFormulaEditorOpen && formulaEdit && (
        <DashboardFormulaEditor startAtEdit formula={formulaEdit} lang={lang} name={user?.name || ''}
          handleClose={() => setIsFormulaEditorOpen(false)}
          handleSaved={async () => { setIsFormulaEditorOpen(false); await refetchFormula(); }} />
      )}
      {isIngredientModalOpen && (
        <IngredientsModal
          open={isIngredientModalOpen}
          data={ingredients}
          allIngredients={allIngredients}
          name={user?.name || ''}
          focusAreaCount={focusTags.length}
          isLoading={isIngredientModalLoading}
          handleClose={() => setIsIngredientModalOpen(false)}
        />
      )}
    </DashboardWrapper>
  );
};

export default Dashboard;
